import { type FC, useRef, useState } from "react";
import { motion } from "motion/react";
import { isEmpty } from "@huub-web/utils";

import type { ToggleInputProps } from "../ToggleInput";
import { IconButton } from "../../IconButton";
import {
  FormInputField,
  type FormInputFieldProps,
} from "../../FormInput/components/FormInputField";
import { ToggleInputDisplayConfiguration } from "../../../services/inputs";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { useDebug } from "../../../hooks/useDebug";
import { FormInputPopupTypeOptions } from "../../../types";
import { Text } from "../../Text";

import {
  cancelButtonStyle,
  fieldAnimationWrapperStyle,
  fieldContainerStyle,
} from "./ToggleInputFieldContainer.css";

export type ToggleInputFieldContainerProps = Pick<
  ToggleInputProps,
  | "label"
  | "value"
  | "type"
  | "selectOptions"
  | "onChange"
  | "error"
  | "success"
  | "highlight"
> & {
  id: string;
  isInvalidType: boolean;
  bounding?: DOMRect;
  onClose?: () => void;
  onCancel?: () => void;
  onValidation?: FormInputFieldProps["onValidation"];
  onOutsideClick?: () => void;
};

/**
 * A container for the input field that is displayed when the user is editing the value of the field.
 */
export const ToggleInputFieldContainer: FC<ToggleInputFieldContainerProps> = ({
  id,
  bounding,
  type = "string",
  label,
  value,
  error,
  success = false,
  highlight = false,
  isInvalidType,
  selectOptions,
  onChange,
  onCancel,
  onClose,
  onValidation,
  onOutsideClick,
}) => {
  const { log } = useDebug();
  const containerRef = useRef<HTMLDivElement>(null);
  const [initialValue] = useState(value);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const minWidth = (() => {
    const minWidth: undefined | number =
      ToggleInputDisplayConfiguration[type]?.minWidth ?? 160;

    return minWidth <= (bounding?.width ?? minWidth) ? undefined : minWidth;
  })();

  const hasChange =
    initialValue?.toString().trim() !== value?.toString().trim();

  const handleClose = () => {
    log("lightBlue", "ToggleInputFieldContainer", "Handle close");
    onClose?.();
  };

  const handleCancel = () => {
    log("lightBlue", "ToggleInputFieldContainer", "Handle cancel");
    onCancel?.();
  };

  const handleOutsideClick = () => {
    onOutsideClick?.();
  };

  useClickOutside<HTMLDivElement | HTMLButtonElement>(
    [wrapperRef, cancelButtonRef],
    () => {
      if (!FormInputPopupTypeOptions.includes(type)) {
        onOutsideClick?.();
      }
    },
  );

  return (
    <motion.div
      ref={containerRef}
      data-type={type}
      transition={{ duration: 0.2 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { delay: 0.1 } }}
      className={fieldContainerStyle}
    >
      <motion.div
        ref={wrapperRef}
        className={fieldAnimationWrapperStyle}
        transition={{ duration: 0.2 }}
        initial={{ width: bounding?.width }}
        animate={{ width: minWidth, transition: { delay: 0.1 } }}
        exit={{ width: bounding?.width }}
      >
        <FormInputField
          id={id}
          fieldKey={id}
          variant="toggle"
          type={type}
          bounding={bounding}
          isInvalidType={isInvalidType}
          label={label}
          value={value}
          onChange={onChange}
          onClose={handleClose}
          onValidation={onValidation}
          onOutsideClick={handleOutsideClick}
          display={
            isEmpty(value) && FormInputPopupTypeOptions.includes(type) ? (
              <Text variant="bodyL">-</Text>
            ) : undefined
          }
          selectOptions={selectOptions}
          error={error}
          highlight={highlight}
          success={success}
          props={ToggleInputDisplayConfiguration[type]?.props}
          closable
        />
        <IconButton
          ref={cancelButtonRef}
          className={cancelButtonStyle}
          icon={hasChange ? "check" : "closeSmall"}
          rounded
          size="xs"
          variant="secondary"
          onClick={handleCancel}
        />
      </motion.div>
    </motion.div>
  );
};
